import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { FileSignature, Send, Download, CheckCircle2, Clock } from "lucide-react";

export default function AgreementDialog({ org, open, onOpenChange, onChanged }) {
  const [ag, setAg] = useState(null);
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!org) return;
    try {
      const { data } = await api.get(`/superadmin/orgs/${org.id}/agreement`);
      setAg(data.agreement || { status: "not_sent" });
    } catch { setAg({ status: "not_sent" }); }
  };
  useEffect(() => { if (open) { setTo(""); load(); } }, [open, org]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/superadmin/orgs/${org.id}/agreement/send`, to.trim() ? { to: to.trim() } : {});
      toast.success(`Agreement sent to ${data.to}`);
      load(); onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send agreement"); }
    finally { setBusy(false); }
  };

  const status = ag?.status || "not_sent";
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none bg-surface border-industrial max-w-lg" data-testid="agreement-dialog">
        <DialogHeader>
          <DialogTitle className="mono uppercase tracking-widest text-sm flex items-center gap-2">
            <FileSignature className="w-4 h-4 text-primary-orange" /> Licence Agreement · <span className="text-primary-orange">{org?.name}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center gap-2" data-testid="agreement-status">
            {status === "signed" ? (
              <span className="inline-flex items-center gap-1.5 text-green-500 text-xs uppercase tracking-widest font-bold"><CheckCircle2 className="w-4 h-4" /> Signed</span>
            ) : status === "sent" ? (
              <span className="inline-flex items-center gap-1.5 text-amber-400 text-xs uppercase tracking-widest font-bold"><Clock className="w-4 h-4" /> Sent · awaiting signature</span>
            ) : (
              <span className="text-muted-industrial text-xs uppercase tracking-widest">Not sent yet</span>
            )}
          </div>

          {status === "signed" ? (
            <div className="border border-industrial p-3 text-xs space-y-1" data-testid="agreement-signed-details">
              <div><span className="text-muted-industrial">Signed by:</span> <b>{ag.signer_name}</b></div>
              <div><span className="text-muted-industrial">Business:</span> {ag.business_name}</div>
              <div><span className="text-muted-industrial">Date entered:</span> {ag.signed_date}</div>
              <div><span className="text-muted-industrial">Timestamp (UTC):</span> {ag.signed_at}</div>
              <div><span className="text-muted-industrial">IP address:</span> {ag.signer_ip}</div>
              <Button data-testid="agreement-download" size="sm" variant="outline" className="rounded-none text-xs mt-2"
                onClick={() => window.open(`${BACKEND}/api/superadmin/orgs/${org.id}/agreement/pdf`, "_blank")}>
                <Download className="w-3.5 h-3.5 mr-1" /> Download signed PDF
              </Button>
            </div>
          ) : (
            <>
              {status === "sent" && (
                <p className="text-xs text-muted-industrial">Sent to <b>{ag.sent_to}</b>{ag.sent_at ? ` on ${new Date(ag.sent_at).toLocaleString()}` : ""}. You can resend below.</p>
              )}
              <div>
                <Label className="label-tag">Recipient email (optional — defaults to the org admin)</Label>
                <Input data-testid="agreement-email" type="email" value={to} onChange={(e) => setTo(e.target.value)}
                  placeholder="client@business.com" className="rounded-none bg-transparent border-industrial mono text-sm" />
              </div>
              <Button data-testid="agreement-send" onClick={send} disabled={busy}
                className="rounded-none bg-primary-orange text-black uppercase text-xs tracking-widest font-bold">
                <Send className="w-3.5 h-3.5 mr-1" /> {busy ? "Sending…" : status === "sent" ? "Resend agreement" : "Send agreement"}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
